/**
 * One diverging bar per day of the selected window, drawn to one count scale
 * so a busy day stands taller than a quiet one: praise above the zero line,
 * criticism below it, neutral split across the line. A tick marks the day's
 * net, praise minus criticism, and the net is printed beneath the bar. A day
 * with no comments is an empty column; a day under the k-gate discloses no
 * stance, so it renders as a dashed placeholder whose reason is a tooltip,
 * and the chart explains withholding once beneath it.
 */

import { Info } from 'lucide-react';
import type { ReactNode } from 'react';

import { HintedIcon } from '../../../chrome/hint';
import { count, net } from '../../../chrome/measure/format';
import { QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { useSentimentSeries, type Window } from '../api/queries';
import type { SentimentBucketStanceCounts, SentimentSeries } from '../api/schemas';
import { st2 } from './messages';
import { SentimentCard } from './SentimentCard';

/** `hour` is refused by the producer (comment stance is stored per date), so
 *  every window reads daily buckets and a sub-day window gets the one or two
 *  dates it spans. */
const BUCKET = 'day';

const DAY_MS = 24 * 60 * 60 * 1000;

const LEGEND = [
  { key: 'supportive', fill: 'bg-primary' },
  { key: 'neutral', fill: 'bg-muted-foreground/40' },
  { key: 'hostile', fill: 'bg-destructive' },
] as const;

interface Day {
  day: string;
  level: string | null;
  n: number;
  counts: SentimentBucketStanceCounts | null;
  withheld: boolean;
}

/** The route's day buckets in order, with an empty day for every date between
 *  two buckets the route returned nothing for: it omits a day with no comment,
 *  and a missing column would read as the neighbours being consecutive. */
function daysOf(series: SentimentSeries): Day[] {
  const buckets = series.points
    .flatMap((point) =>
      'bucket_start' in point
        ? [
            {
              day: point.bucket_start,
              level: point.influence_scope.level,
              n: point.n,
              counts: point.withheld ? null : point.stance_counts,
              withheld: point.withheld,
            },
          ]
        : [],
    )
    .sort((a, b) => a.day.localeCompare(b.day));
  const days: Day[] = [];
  for (const bucket of buckets) {
    const previous = days.at(-1);
    if (previous !== undefined) {
      for (
        let at = Date.parse(`${previous.day}T00:00:00Z`) + DAY_MS;
        at < Date.parse(`${bucket.day}T00:00:00Z`);
        at += DAY_MS
      ) {
        const day = new Date(at).toISOString().slice(0, 10);
        days.push({ day, level: bucket.level, n: 0, counts: null, withheld: false });
      }
    }
    days.push(bucket);
  }
  return days;
}

/** Each half's height, praise or criticism plus half the neutral count. */
function halves(counts: SentimentBucketStanceCounts): { up: number; down: number } {
  return {
    up: counts.supportive + counts.neutral / 2,
    down: counts.hostile + counts.neutral / 2,
  };
}

function dayLabel(date: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

function netTone(value: number): string {
  if (value > 0) return 'text-primary-ink';
  if (value < 0) return 'text-destructive';
  return 'text-muted-foreground';
}

function Bars(props: {
  counts: SentimentBucketStanceCounts;
  scale: number;
  label: string;
}): ReactNode {
  const { counts, scale } = props;
  const { up, down } = halves(counts);
  const balance = counts.supportive - counts.hostile;
  const percent = (value: number) => `${(value / scale) * 50}%`;
  return (
    <HintedIcon label={props.label} className="absolute inset-0 block">
      <span
        className="absolute inset-x-0 bottom-1/2 flex flex-col overflow-hidden rounded-t-sm"
        style={{ height: percent(up) }}
      >
        <span
          data-segment="supportive"
          className="bg-primary"
          style={{ flexGrow: counts.supportive }}
        />
        <span
          data-segment="neutral"
          className="bg-muted-foreground/40"
          style={{ flexGrow: counts.neutral }}
        />
      </span>
      <span
        className="absolute inset-x-0 top-1/2 flex flex-col overflow-hidden rounded-b-sm"
        style={{ height: percent(down) }}
      >
        <span
          data-segment="neutral"
          className="bg-muted-foreground/40"
          style={{ flexGrow: counts.neutral }}
        />
        <span
          data-segment="hostile"
          className="bg-destructive"
          style={{ flexGrow: counts.hostile }}
        />
      </span>
      <span
        data-slot="net-tick"
        className="bg-foreground absolute -inset-x-0.5 h-0.5 -translate-y-1/2 rounded-pill"
        style={{ top: `calc(50% - ${percent(balance)})` }}
      />
    </HintedIcon>
  );
}

function Column(props: { day: Day; scale: number }): ReactNode {
  const { day, scale } = props;
  const label = dayLabel(day.day);
  const locale = getLocale();
  const counts = day.counts;
  const balance = counts === null ? null : counts.supportive - counts.hostile;
  return (
    <li aria-label={label} className="flex min-w-0 flex-1 flex-col items-center gap-1">
      <div className="relative h-44 w-full">
        {day.withheld && day.n > 0 ? (
          <HintedIcon
            label={st2('daily.withheld')}
            className="border-muted-foreground/40 bg-muted/30 absolute inset-x-0 top-1/2 block h-6 -translate-y-1/2 rounded-sm border border-dashed"
          >
            {null}
          </HintedIcon>
        ) : counts !== null && day.n > 0 ? (
          <Bars
            counts={counts}
            scale={scale}
            label={st2('daily.bar', {
              bucket: label,
              supportive: count(counts.supportive, locale),
              neutral: count(counts.neutral, locale),
              hostile: count(counts.hostile, locale),
              net: net(counts.supportive - counts.hostile, locale),
            })}
          />
        ) : null}
      </div>
      {/* A phone fits a week of columns only by their day of the month. */}
      <span aria-hidden data-numeric className="text-caption text-muted-foreground">
        <span className="sm:hidden">{day.day.slice(8)}</span>
        <span className="hidden sm:inline">{label}</span>
      </span>
      <span
        data-numeric
        data-slot="net"
        className={`text-caption ${balance === null || day.n === 0 ? 'invisible' : netTone(balance)}`}
      >
        {balance === null || day.n === 0 ? '·' : net(balance, locale)}
      </span>
    </li>
  );
}

function Legend(): ReactNode {
  return (
    <ul aria-label={st2('daily.legend')} className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {LEGEND.map((item) => (
        <li key={item.key} className="text-caption text-muted-foreground flex items-center gap-1">
          <span aria-hidden className={`size-2.5 rounded-sm ${item.fill}`} />
          {st2(`daily.${item.key}`)}
        </li>
      ))}
      <li className="text-caption text-muted-foreground flex items-center gap-1">
        <span aria-hidden className="bg-foreground h-0.5 w-3 rounded-pill" />
        {st2('daily.net')}
      </li>
    </ul>
  );
}

export function DailyStanceView(props: { series: SentimentSeries }): ReactNode {
  const days = daysOf(props.series);
  const scale = Math.max(
    1,
    ...days.flatMap((day) => {
      if (day.counts === null) return [];
      const { up, down } = halves(day.counts);
      return [up, down];
    }),
  );
  return (
    <>
      <div className="relative">
        <div aria-hidden className="bg-border absolute inset-x-0 top-22 h-px" />
        <ul aria-label={st2('daily.label')} className="relative flex gap-2">
          {days.map((day) => (
            <Column key={`${day.day}-${day.level ?? ''}`} day={day} scale={scale} />
          ))}
        </ul>
      </div>
      <Legend />
      {days.some((day) => day.withheld && day.n > 0) && (
        <p className="text-caption text-muted-foreground">{st2('daily.withheldNote')}</p>
      )}
    </>
  );
}

function hasNothing(series: SentimentSeries): boolean {
  return daysOf(series).every((day) => day.n === 0);
}

export function DailyStanceCard(props: {
  campaignId: string;
  tenantId: string;
  scopeRef: string;
  window: Window;
}): ReactNode {
  const query = useSentimentSeries(props.campaignId, props.tenantId, {
    scopeRef: props.scopeRef,
    window: props.window,
    bucket: BUCKET,
  });
  // The headline already says when the window has no reading, so an empty chart says nothing more.
  if (query.status === 'success' && hasNothing(query.data)) return null;
  return (
    <SentimentCard
      title={st2('daily.title')}
      aside={
        <HintedIcon label={st2('daily.readers')} className="text-muted-foreground">
          <Info aria-hidden className="size-4" />
        </HintedIcon>
      }
    >
      <QueryStates query={query}>{(data) => <DailyStanceView series={data} />}</QueryStates>
    </SentimentCard>
  );
}
